import { useEffect } from 'react';
import { useRecoilState } from 'recoil';
import { activeNavCategorySetState } from '../../recoil/atoms';
import { ProductTag } from '../../enums/ProductTag.enum';
import { AssetPathFn } from '../../models/CommonFunctions';
import { NavCategorySet } from '../../models/NavCategorySet.model';
import { Collections } from '../../models/Collection.model';
import { NavCategory } from '../../enums/NavCategory.enum';
import { NavCategoryContext } from '../../enums/NavCategoryContext.enum';
import { enforceType } from '../../sizing-tool/utils/array-helper';

// NavCategorySets[] are used as menu items for a given NavCategoryContext (Collections, Mobile Nav, etc)
// A NavCategorySet has many Collections through the ProductTag key
// A Collection represents a set of type garments (or a card on the collections page)

export const useNavCategories = ({
  context = NavCategoryContext.LandingPage,
  setCategoryFromPath = false,
  initializeCategory,
}: {
  context: NavCategoryContext;
  setCategoryFromPath?: boolean;
  initializeCategory?: NavCategory;
}): { navCategorySets: NavCategorySet[]; activeNavCategorySet: NavCategorySet } => {
  const filteredNavCategorySets = filterNavCategories(context);
  const [activeNavCategorySet, setNavCategorySet] = useRecoilState(activeNavCategorySetState);

  useEffect(() => {
    if (typeof window !== 'undefined' && setCategoryFromPath) {
      const pathname = window.document.location.pathname;
      const navCategory = pathToCollectionCategory(pathname);
      setNavCategorySet(navCategorySets.find((c) => c.navCategory === navCategory) ?? allNavCategorySet);
    } else if (!!initializeCategory) {
      setNavCategorySet(navCategorySets.find((c) => c.navCategory === initializeCategory) ?? allNavCategorySet);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { navCategorySets: filteredNavCategorySets, activeNavCategorySet };
};

export const allCollections: Collections = {
  [enforceType<ProductTag>('collection_shapewear')]: {
    title: 'Sculptwear',
    productTag: enforceType<ProductTag>('collection_shapewear'),
    description:
      'Take your shapewear to the next level. Targeted compression sculpts without squeezing, and flexible boning prevents roll-down. Breathable, sweat-wicking fabric keeps you cool. Lingerie-inspired designs are just as sexy as they are supportive.',
  },
  [enforceType<ProductTag>('collection_bras')]: {
    title: 'Bras',
    productTag: enforceType<ProductTag>('collection_bras'),
    description:
      'Say bye to underwire for good thanks to bonded bust support. Our bras are made with super soft yet strong fabric, flattering cuts that eliminate bulges, and stylish details that set them apart. Supports cup sizes A - DDD.',
  },
  [enforceType<ProductTag>('collection_tops')]: {
    title: 'Tops',
    productTag: enforceType<ProductTag>('collection_tops'),
    description:
      'Built-in, underwire-free bust support provides comfortable lift so you can ditch your bra without worry. Whether you’re looking for high compression sculpting or all-over smoothing, there’s a top that’s perfect for you.',
  },
  [enforceType<ProductTag>('collection_bodysuits')]: {
    title: 'Bodysuits',
    productTag: enforceType<ProductTag>('collection_bodysuits'),
    description:
      'These curve-hugging garments never come untucked. An adjustable gusset lets you achieve your perfect length. Featuring underwire-free bust support and a wedgie-free brief.',
  },
  [enforceType<ProductTag>('collection_bottoms')]: {
    title: 'Bottoms',
    productTag: enforceType<ProductTag>('collection_bottoms'),
    description:
      'Hidden shaping panels target your lower tummy and thighs. Luxe, stretch fabrics hug your curves and flatter your figure. Dress them up or dress them down for effortlessly chic style, no matter what.',
  },
  [enforceType<ProductTag>('collection_underwear')]: {
    title: 'Underwear',
    productTag: enforceType<ProductTag>('collection_underwear'),
    description: 'Upgrade your underwear drawer with silky soft fabrics and waist accentuating designs.',
  },
  [enforceType<ProductTag>('collection_bestsellers')]: {
    title: 'Bestsellers',
    productTag: enforceType<ProductTag>('collection_bestsellers'),
    description:
      'Shop our most popular garments. These bestselling pieces are tried and true by you. See for yourself why everyone is buzzing about Honeylove.',
  },
};

export const allNavCategorySet = {
  title: 'All',
  productTags: [
    enforceType<ProductTag>('collection_shapewear'),
    enforceType<ProductTag>('collection_bras'),
    enforceType<ProductTag>('collection_tops'),
    enforceType<ProductTag>('collection_bottoms'),
    enforceType<ProductTag>('collection_bodysuits'),
    enforceType<ProductTag>('collection_underwear'),
  ],
  link: '/products',
  navCategory: NavCategory.All,
  order: {
    [NavCategoryContext.CollectionsPage]: 1,
    [NavCategoryContext.DesktopMenu]: 1,
  },
};

export const navCategorySets: NavCategorySet[] = [
  allNavCategorySet,
  {
    title: 'Sculptwear',
    productTags: [enforceType<ProductTag>('collection_shapewear')],
    link: '/collections/sculptwear',
    navCategory: NavCategory.Sculptwear,
    order: {
      [NavCategoryContext.LandingPage]: 2,
      [NavCategoryContext.CollectionsPage]: 3,
      [NavCategoryContext.MobileMenu]: 2,
      [NavCategoryContext.DesktopMenu]: 3,
    },
  },
  {
    title: 'Bras',
    productTags: [enforceType<ProductTag>('collection_bras')],
    link: '/collections/bras',
    navCategory: NavCategory.Bras,
    order: {
      [NavCategoryContext.LandingPage]: 3,
      [NavCategoryContext.CollectionsPage]: 4,
      [NavCategoryContext.MobileMenu]: 3,
      [NavCategoryContext.DesktopMenu]: 4,
    },
  },
  {
    title: 'Tops',
    productTags: [enforceType<ProductTag>('collection_tops')],
    link: '/collections/tops',
    navCategory: NavCategory.Tops,
    order: {
      [NavCategoryContext.LandingPage]: 4,
      [NavCategoryContext.CollectionsPage]: 5,
      [NavCategoryContext.MobileMenu]: 4,
      [NavCategoryContext.DesktopMenu]: 5,
    },
  },
  {
    title: 'Bottoms',
    productTags: [enforceType<ProductTag>('collection_bottoms')],
    link: '/collections/bottoms',
    navCategory: NavCategory.Bottoms,
    order: {
      [NavCategoryContext.LandingPage]: 5,
      [NavCategoryContext.CollectionsPage]: 7,
      [NavCategoryContext.MobileMenu]: 5,
      [NavCategoryContext.DesktopMenu]: 7,
    },
  },
  {
    title: 'Bodysuits',
    productTags: [enforceType<ProductTag>('collection_bodysuits')],
    link: '/collections/bodysuits',
    navCategory: NavCategory.Bodysuits,
    order: {
      [NavCategoryContext.LandingPage]: 6,
      [NavCategoryContext.CollectionsPage]: 6,
      [NavCategoryContext.MobileMenu]: 6,
      [NavCategoryContext.DesktopMenu]: 6,
    },
  },
  {
    title: 'Underwear',
    productTags: [enforceType<ProductTag>('collection_underwear')],
    link: '/collections/underwear',
    navCategory: NavCategory.Underwear,
    order: {
      [NavCategoryContext.LandingPage]: 7,
      [NavCategoryContext.CollectionsPage]: 8,
      [NavCategoryContext.MobileMenu]: 7,
      [NavCategoryContext.DesktopMenu]: 8,
    },
  },
  {
    title: 'Bestsellers',
    productTags: [enforceType<ProductTag>('collection_bestsellers')],
    link: '/collections/bestsellers',
    navCategory: NavCategory.Bestsellers,
    order: {
      [NavCategoryContext.LandingPage]: 1,
      [NavCategoryContext.CollectionsPage]: 2,
      [NavCategoryContext.MobileMenu]: 1,
      [NavCategoryContext.DesktopMenu]: 2,
    },
  },
  {
    title: 'Sale',
    productTags: [
      enforceType<ProductTag>('collection_shapewear'),
      enforceType<ProductTag>('collection_bras'),
      enforceType<ProductTag>('collection_tops'),
      enforceType<ProductTag>('collection_bottoms'),
      enforceType<ProductTag>('collection_bodysuits'),
      enforceType<ProductTag>('collection_underwear'),
    ],
    link: '/collections/discounted',
    navCategory: NavCategory.Discounted,
    order: {},
  },
];

export const navCategoryImageLookupFn =
  (assetPathFn: AssetPathFn) =>
  (navCategory: NavCategory, size: 'large' | 'small'): string | undefined => {
    const suffix = size === 'large' ? 'jpg' : 'png';
    switch (navCategory) {
      case NavCategory.Sculptwear:
        return assetPathFn(`header-category-sculptwear-${size}.${suffix}`);
      case NavCategory.Bras:
        return assetPathFn(`header-category-bras-${size}.${suffix}`);
      case NavCategory.Tops:
        return assetPathFn(`header-category-tops-${size}.${suffix}`);
      case NavCategory.Bodysuits:
        return assetPathFn(`header-category-bodysuits-${size}.${suffix}`);
      case NavCategory.Bottoms:
        return assetPathFn(`header-category-bottoms-${size}.${suffix}`);
      case NavCategory.Underwear:
        return assetPathFn(`header-category-underwear-${size}.${suffix}`);
      case NavCategory.Bestsellers:
        return assetPathFn(`header-category-bestsellers-${size}.${suffix}`);
      case NavCategory.Discounted:
        return assetPathFn(`header-category-discounted-${size}.${suffix}`);
      case NavCategory.All:
        return assetPathFn(`header-category-all-${size}.${suffix}`);
      default:
        undefined;
    }
  };

const filterNavCategories = (navCategoryContext: NavCategoryContext): NavCategorySet[] =>
  navCategorySets.filter(contextFilter(navCategoryContext)).sort(contextSort(navCategoryContext));

const contextFilter =
  (navCategoryContext: NavCategoryContext) =>
  ({ order }: NavCategorySet): boolean =>
    typeof order[navCategoryContext] !== 'undefined';

const contextSort =
  (navCategoryContext: NavCategoryContext) =>
  (a: NavCategorySet, b: NavCategorySet): -1 | 0 | 1 => {
    const aValue = a.order[navCategoryContext] ?? 999;
    const bValue = b.order[navCategoryContext] ?? 999;
    return aValue === bValue ? 0 : aValue > bValue ? 1 : -1;
  };

const pathToCollectionCategory = (pathname: string): NavCategory | undefined => {
  const match = pathname.match(/^\/collections\/(\w+)/i);
  const category = match && match.length > 1 ? match[1].toLowerCase() : '';
  switch (category) {
    case 'sculptwear':
      return NavCategory.Sculptwear;
    case 'liftwear':
      return NavCategory.LiftWear;
    case 'bras':
      return NavCategory.Bras;
    case 'tops':
      return NavCategory.Tops;
    case 'bodysuits':
      return NavCategory.Bodysuits;
    case 'underwear':
      return NavCategory.Underwear;
    case 'bottoms':
      return NavCategory.Bottoms;
    case 'discounted':
      return NavCategory.Discounted;
    case 'bestsellers':
      return NavCategory.Bestsellers;
    default:
      return undefined;
  }
};
